import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@/components/ui';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { fetchRoleMapImpact } from '@/api/console';
import type { RoleMapGroup } from './types';

/**
 * EAA-1837: confirm step for "Xoá" in the selection toolbar.
 *
 * It exists, but WITHOUT the typed-phrase gate `accounts/BulkDeleteConfirmModal`
 * uses: this delete is the server's SOFT delete (`RoleMapService.bulk_deactivate`
 * flips `is_active=False`, never a row removal), so it is not the irreversible
 * identity destruction that gate was built for. What it still is, is a
 * provisioning change — the accounts named below lose these app roles at their
 * next grant — and that blast radius is what this dialog states.
 *
 * EAA-1817: the client-derived `affectedAccounts` estimate below (from the
 * page's own account-count walk, capped and approximate — see
 * `ACCOUNT_COUNT_MAX_PAGES` in `helpers.ts`) is now backed, where possible, by
 * the server's real `GET /role-maps/{id}/impact` (`account_count` = DISTINCT
 * `user_sub`, never a guess). "Where possible" is load-bearing: that endpoint
 * is `IsSuperAdmin`-ONLY server-side (`RoleMapImpactView`) even though the SRS
 * describes org-admins getting their own org's data too — the code is the
 * source of truth here, not the SRS. `console.rolemap.manage` is issued
 * EXCLUSIVELY to super-admins (never to org-admins — see portal-api
 * `directory/authz.py`'s `ORG_ADMIN_CONSOLE_CAPABILITIES`), so gating the
 * fetch on that capability (read through `useCan()`, never `/me/admin`'s
 * `is_super_admin` — D10) mirrors the server's real boundary rather than the
 * SRS's incorrect one. Org-admins simply never see the section below; the
 * modal stays usable with just the client-side estimate it always had.
 */
export function RoleMapDeleteConfirmModal({
    open,
    groups,
    linkCount,
    pending,
    onCancel,
    onConfirm,
}: {
    open: boolean;
    groups: RoleMapGroup[];
    linkCount: number;
    pending: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    const { t } = useTranslation();
    const affectedAccounts = groups.reduce((sum, group) => sum + group.accountCount, 0);

    const can = useCan();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const canSeeImpact = can('console.rolemap.manage');
    const impactIds = useMemo(() => groups.flatMap((group) => group.ids), [groups]);
    const impactEnabled = open && canSeeImpact && Boolean(accessToken) && impactIds.length > 0;

    const impactQueries = useQueries({
        queries: impactIds.map((id) => ({
            queryKey: ['role-map-impact', accessToken, id],
            queryFn: () => fetchRoleMapImpact(config.apiBase, accessToken as string, id),
            enabled: impactEnabled,
            retry: false,
        })),
    });

    const impactLoading = impactEnabled && impactQueries.some((q) => q.isLoading);
    // Never render a guessed 0: while any relevant fetch is still in flight the
    // section says "…" instead of an undercount that looks final. Errored
    // fetches are silently excluded from the sum (degrade gracefully) rather
    // than blocking the whole confirm dialog over one failed lookup.
    const impactAccountCount = impactEnabled
        ? impactQueries.reduce((sum, q) => sum + (q.data?.account_count ?? 0), 0)
        : null;
    const remainingAppRoles = useMemo(() => {
        if (!impactEnabled) return [];
        const set = new Set<string>();
        for (const q of impactQueries) {
            for (const role of q.data?.remaining_app_roles ?? []) set.add(role);
        }
        return Array.from(set);
    }, [impactEnabled, impactQueries]);

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            title={t('console.roleMap.deleteConfirmModal.title', { count: groups.length })}
            footer={
                <>
                    <Button onClick={onCancel} disabled={pending}>
                        {t('common.cancel')}
                    </Button>
                    {/* EAA-1817: kit `Button danger`, not `ConfirmButton` — this whole
                    Modal (impact estimate, affected accounts, remaining app roles) IS
                    already the confirmation surface; nesting `ConfirmButton`'s
                    `Popconfirm` here would ask the operator to confirm a confirmation. */}
                    <Button danger onClick={onConfirm} disabled={pending}>
                        {pending
                            ? t('console.roleMap.deleteConfirmModal.deleting')
                            : t('console.roleMap.deleteConfirmModal.confirmButton')}
                    </Button>
                </>
            }
        >
            <div className="rm-delete-confirm">
                <p className="rm-delete-confirm__text">
                    {t('console.roleMap.deleteConfirmModal.softDeleteText', { count: linkCount })}
                </p>
                {canSeeImpact ? (
                    <p className="rm-delete-confirm__text">
                        <strong>{impactLoading ? '…' : impactAccountCount}</strong>{' '}
                        {t('console.roleMap.deleteConfirmModal.impactSuffix')}
                    </p>
                ) : (
                    affectedAccounts > 0 && (
                        <p className="rm-delete-confirm__text">
                            {t('console.roleMap.deleteConfirmModal.approxPrefix')}{' '}
                            <strong>{affectedAccounts}</strong>{' '}
                            {t('console.roleMap.deleteConfirmModal.impactSuffix')}
                        </p>
                    )
                )}
                {!impactLoading && remainingAppRoles.length > 0 && (
                    <p className="rm-delete-confirm__text">
                        {t('console.roleMap.deleteConfirmModal.remainingRolesPrefix')}{' '}
                        {remainingAppRoles.map((role, index) => (
                            <span key={role}>
                                {index > 0 && ', '}
                                <code>{role}</code>
                            </span>
                        ))}
                        .
                    </p>
                )}
                <ul className="rm-delete-confirm__list">
                    {groups.map((group) => (
                        <li key={group.key}>
                            <code>{group.role_key}</code> → {group.appName}:{' '}
                            {group.appRoles.map((chip) => chip.app_role).join(', ')}
                        </li>
                    ))}
                </ul>
            </div>
        </Modal>
    );
}
